import { useCallback, useEffect, useState } from 'react'
import {
  CommissionDetails,
  StaffUpdateCommissionInfoForCampaignRequest,
  StaffUpdateCommissionInfoForCampaignRequestChangeType,
  useInvalidateUseStaffGetCommissionInfoForCampaign,
  useStaffUpdateCommissionInfoForCampaign,
} from '@urbanx/agx.campaigns.client'
import {
  AgxButton,
  AgxCommissionPlan,
  AgxModal,
  AgxRow,
  AgxSpacer,
  AgxTypography,
  Icon_FeaturedIcon,
} from '@urbanx/agx.ui-components2'
import EditCommissionReason, { ChangeDetails } from './EditCommissionReason'
import { useSelectedCampaign } from 'hooks'
import {
  allowedCommissionTypes,
  EditCommissionModalPage,
} from './CommissionEnums'

const EditCommissionModal = ({
  onClose,
  currentCommissionDetails,
}: {
  onClose: () => void
  currentCommissionDetails: CommissionDetails
}) => {
  const [currentPage, setCurrentPage] = useState<EditCommissionModalPage>(
    EditCommissionModalPage.Reason
  )

  const [reasonDetails, setReasonDetails] = useState<ChangeDetails | undefined>(
    undefined
  )
  const [commissionDetails, setCommissionDetails] = useState<CommissionDetails>(
    () => currentCommissionDetails
  )

  const [reasonDetailsValid, setReasonDetailsValid] = useState(false)
  const [commissionDetailsValid, setCommissionDetailsValid] = useState(false)

  useEffect(() => {
    if (!reasonDetails?.reasonType) {
      setReasonDetailsValid(false)
      return
    }

    const requiresEvidence =
      reasonDetails.reasonType ===
        StaffUpdateCommissionInfoForCampaignRequestChangeType.CommissionReduction ||
      reasonDetails.reasonType ===
        StaffUpdateCommissionInfoForCampaignRequestChangeType.VendorChoiceConfirmed

    const hasEvidence = (reasonDetails.evidenceUpload?.files?.length ?? 0) > 0

    const hasOtherReason =
      reasonDetails.reasonType !==
        StaffUpdateCommissionInfoForCampaignRequestChangeType.Other ||
      !!reasonDetails.reason?.trim()

    const isUploading = reasonDetails.evidenceUpload?.isUploading ?? false

    setReasonDetailsValid(
      !isUploading && hasOtherReason && (!requiresEvidence || hasEvidence)
    )
  }, [
    reasonDetails?.reasonType,
    reasonDetails?.reason,
    reasonDetails?.evidenceUpload?.files?.length,
    reasonDetails?.evidenceUpload?.isUploading,
  ])

  const invalidateCommissionInfo =
    useInvalidateUseStaffGetCommissionInfoForCampaign()

  const { campaign: selectedCampaign } = useSelectedCampaign()

  const {
    staffUpdateCommissionInfoForCampaign,
    staffUpdateCommissionInfoForCampaignPending,
  } = useStaffUpdateCommissionInfoForCampaign({
    onSuccess: async () => {
      await invalidateCommissionInfo(selectedCampaign?.campaignId || '')
      setCurrentPage(EditCommissionModalPage.UpdateSuccess)
    },
    onError: (error) => {
      console.error('Error updating commission info:', error)
    },
  })

  useEffect(() => {
    if (currentPage === EditCommissionModalPage.UpdateSuccess) {
      const timer = setTimeout(() => {
        onClose()
        setCurrentPage(EditCommissionModalPage.Reason)
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [currentPage])

  const onReasonDetailsUpdated = useCallback(
    (updatedDetails: ChangeDetails) => {
      setReasonDetails(updatedDetails)
    },
    [setReasonDetails]
  )

  const onCommissionUpdated = useCallback(
    (updatedCommission: { value: CommissionDetails }) => {
      setCommissionDetails(updatedCommission.value)
    },
    [setCommissionDetails]
  )

  return (
    <AgxModal
      minWidth="800px"
      maxWidth="800px"
      title={
        currentPage === EditCommissionModalPage.UpdateSuccess
          ? ''
          : currentPage === EditCommissionModalPage.Reason
            ? 'Provide reason for commission update'
            : 'Enter new commission rate'
      }
      type="single"
      variant={
        currentPage === EditCommissionModalPage.UpdateSuccess
          ? 'withoutDivider'
          : 'withDivider'
      }
      actionButtonContainerStyle={'withoutDivider'}
      content={
        <AgxRow
          gap="none"
          padding={{ top: 'md', bottom: 'md', left: 'xl', right: 'xl' }}
        >
          {currentPage === EditCommissionModalPage.Reason && (
            <EditCommissionReason
              defaultChangeDetails={reasonDetails}
              onChange={onReasonDetailsUpdated}
              campaignId={selectedCampaign?.campaignId || ''}
              campaignAddress={selectedCampaign?.address}
            />
          )}
          {currentPage === EditCommissionModalPage.Details && (
            <AgxCommissionPlan
              id={'commissionPlan'}
              commissionTypes={allowedCommissionTypes}
              onValueChanged={onCommissionUpdated}
              formName={'Agency Agreement'}
              defaultValue={commissionDetails}
              required
              showCommissionRangeDisclaimer
              onValidateStatusChange={setCommissionDetailsValid}
            />
          )}
          {currentPage === EditCommissionModalPage.UpdateSuccess && (
            <AgxSpacer
              direction="vertical"
              gap={'spacing-lg'}
              contentAlignment="center"
              contentJustification="center"
              fill
            >
              <Icon_FeaturedIcon />
              <AgxTypography
                id="commissionUpdateSuccessMessage"
                type="body_ui_strong"
                size="medium"
                color="var(--text-primary)"
              >
                Commission updated successfully!
              </AgxTypography>
            </AgxSpacer>
          )}
        </AgxRow>
      }
      setIsDialogOpen={onClose}
      actionButtons={
        <>
          {currentPage === EditCommissionModalPage.Reason && (
            <AgxSpacer
              direction="horizontal"
              gap="spacing-lg"
              fill
              contentAlignment="baseline"
              contentJustification="center"
            >
              <AgxButton
                id="commissionReasonCancel"
                text="Cancel"
                variant="hollow"
                onClick={onClose}
                size="large"
                stretch
              />
              <AgxButton
                id="commissionReasonNext"
                text="Next"
                variant="primary"
                onClick={() => setCurrentPage(EditCommissionModalPage.Details)}
                disabled={!reasonDetailsValid}
                size="large"
                stretch
              />
            </AgxSpacer>
          )}
          {currentPage === EditCommissionModalPage.Details && (
            <AgxSpacer
              direction="horizontal"
              gap="spacing-lg"
              fill
              contentAlignment="baseline"
              contentJustification="center"
            >
              <AgxButton
                id="commissionDetailsBack"
                text="Back"
                variant="hollow"
                onClick={() => setCurrentPage(EditCommissionModalPage.Reason)}
                size="large"
                stretch
                disabled={staffUpdateCommissionInfoForCampaignPending}
              />
              <AgxButton
                id="commissionDetailsSave"
                text="Save"
                variant="primary"
                onClick={async () => {
                  if (!commissionDetails || !reasonDetails?.reasonType) return
                  const changeEvidence =
                    reasonDetails.evidenceUpload?.files?.[0]
                  await staffUpdateCommissionInfoForCampaign({
                    campaignId: selectedCampaign?.campaignId || '',
                    commissionDetails,
                    changeType: reasonDetails.reasonType,
                    reason:
                      reasonDetails.reasonType ===
                      StaffUpdateCommissionInfoForCampaignRequestChangeType.Other
                        ? reasonDetails.reason
                        : undefined,
                    changeEvidence,
                  } as StaffUpdateCommissionInfoForCampaignRequest)
                }}
                disabled={
                  !commissionDetailsValid ||
                  !reasonDetailsValid ||
                  staffUpdateCommissionInfoForCampaignPending
                }
                size="large"
                stretch
              />
            </AgxSpacer>
          )}
        </>
      }
    />
  )
}

export default EditCommissionModal
